import {
  RefreshCw, FileBarChart, Sparkles, ClipboardList, BarChart3, Upload, Database,
  ShieldCheck, DollarSign, Activity, Bell, ShieldAlert, Grid3X3, CalendarDays,
  Globe, Landmark, Eye, Briefcase,
  type LucideIcon,
} from 'lucide-react';

export type ModuleCategory = 'portfolio' | 'analysis' | 'market' | 'strategy';

export interface DashboardModule {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  /** Tailwind color stem (e.g. 'sky', 'emerald'). Used for ring/border/bg/text accents. */
  color: string;
  /** Route to navigate on click. Omit for non-route modules (e.g. inline triggers). */
  route?: string;
  /** Optional click action used instead of route navigation. */
  action?: 'refresh-prices' | 'open-excel-import';
  category: ModuleCategory;
  bullets: Array<string | { bold: string; text: string }>;
}

export const DASHBOARD_MODULES: DashboardModule[] = [
  {
    id: 'portfolio-dashboard',
    title: 'Portfolio Dashboard',
    description: 'Central hub for real-time portfolio monitoring',
    icon: RefreshCw,
    color: 'primary',
    action: 'refresh-prices',
    category: 'portfolio',
    bullets: [
      'Live stock price refresh with visual progress bar & success rate',
      'Portfolio stats: total value (CHF), gains, capital recovery %, added value',
      '3-tier alert system (15% / 30% / 45%) with sell recommendations',
      'Stock scorecard, Fibonacci retracement & gains pie chart',
      'Auto-refresh every 60 min with countdown timer',
    ],
  },
  {
    id: 'ibkr-full-analysis',
    title: 'IBKR Full Analysis PF',
    description: 'Interactive Brokers PDF statement deep-dive',
    icon: FileBarChart,
    color: 'accent',
    route: '/ibkr-import',
    category: 'portfolio',
    bullets: [
      'Upload & parse IBKR PDF statements for positions, trades & dividends',
      'Cash balances, deposits, withdrawals, interest & fee tracking',
      'Full financial summary with realised P&L breakdown',
      'Multi-currency support (USD, EUR, CHF) with live conversion',
      'Historical import archive with cloud persistence & email reports',
    ],
  },
  {
    id: 'content-summary',
    title: 'Content Summary',
    description: 'AI-powered YouTube & video analysis engine',
    icon: Sparkles,
    color: 'red',
    route: '/content-summary',
    category: 'market',
    bullets: [
      'Paste any YouTube URL for instant AI-generated structured summary',
      'Deep-dive, key quotes, compare & playlist analysis modes',
      'Follow-up chat for drilling into specific topics',
      'Auto-save summaries to cloud with diff view for revisions',
      'Real-time streaming output with copy-to-clipboard',
    ],
  },
  {
    id: 'db-order',
    title: 'DB Order',
    description: 'Trading order creation, PDF generation & email',
    icon: ClipboardList,
    color: 'orange',
    route: '/db-order',
    category: 'strategy',
    bullets: [
      'Create Buy/Sell orders with ISIN, stock exchange code & limits',
      'Normal & Limit order types with validity date control',
      'Auto-generated PDF order form emailed to portfolio manager',
      'Order archive with status tracking (Pending → Executed) & PDF download',
      'Multi-portfolio support across CHF, EUR & USD accounts',
    ],
  },
  {
    id: 'ibkr-surveillance',
    title: 'IBKR Surveillance',
    description: 'SMA crossover & Ichimoku Cloud technical scanning',
    icon: BarChart3,
    color: 'purple',
    route: '/sma-surveillance',
    category: 'analysis',
    bullets: [
      'SMA crossover detection (20/50/200-day) with Buy/Sell signals',
      'Ichimoku Cloud analysis with 5-component scoring system',
      'Interactive price history charts per stock with overlays',
      'Batch analysis across entire portfolio with signal summary',
      'Automated scan reports delivered via email',
    ],
  },
  {
    id: 'excel-import',
    title: 'Excel Import',
    description: 'PRVFIN spreadsheet parsing & position sync',
    icon: Upload,
    color: 'cyan',
    action: 'open-excel-import',
    category: 'portfolio',
    bullets: [
      'Direct .xlsx/.xls import from PRVFIN tab with configurable row range',
      'Auto currency detection & live exchange rate conversion to CHF',
      'Preview with position count, total value & validation checks',
      'ISIN-to-stock-code translation via OpenFIGI API',
      'Revolut portfolio import support with auto-mapping',
    ],
  },
  {
    id: 'collected-data',
    title: 'Collected Data',
    description: 'AI web search history & research archive',
    icon: Database,
    color: 'teal',
    route: '/collected-data',
    category: 'market',
    bullets: [
      'AI-powered web search with structured, sourced results',
      'Full search history with source URLs, metadata & timestamps',
      'Multi-model AI responses (Gemini, GPT) with model selection',
      'Persistent cloud storage of all research for future reference',
    ],
  },
  {
    id: 'hedge-protection',
    title: 'Hedge Protection',
    description: 'Multi-LLM options hedging & strategy simulator',
    icon: ShieldCheck,
    color: 'emerald',
    route: '/hedge-protection',
    category: 'analysis',
    bullets: [
      'Multi-LLM analysis (up to 4 AI models in parallel) with consensus scoring',
      'Protective Puts, Collars, Covered Calls & Short ETF strategies',
      'Actionable Options Order Book with strike, expiry & premium details',
      'Greeks explainer & strategy P&L comparison charts',
      'Auto-save sessions with restore, history & email reports',
    ],
  },
  {
    id: 'dividend-dashboard',
    title: 'Dividend Dashboard',
    description: 'Income tracking, calendar & yield analysis',
    icon: DollarSign,
    color: 'amber',
    route: '/dividend-dashboard',
    category: 'analysis',
    bullets: [
      'Total dividend income with yield on cost & forward yield',
      'Monthly income bar chart & cumulative trend visualization',
      'Dividend calendar with ex-date & payment date tracking',
      'Top dividend payers ranked with projected annual income',
      'FMP & Nasdaq data sources with fallback badge indicator',
    ],
  },
  {
    id: 'performance-tracker',
    title: 'Performance Tracker',
    description: 'Portfolio returns, benchmarking & attribution',
    icon: Activity,
    color: 'blue',
    route: '/performance-tracker',
    category: 'analysis',
    bullets: [
      'Overall portfolio return with best & worst performer highlights',
      'Return by position horizontal bar chart with sorting',
      'Invested vs current value comparison per holding',
      'Added value in CHF across all positions with trend',
      'Portfolio snapshot history for time-series comparison',
    ],
  },
  {
    id: 'price-alerts',
    title: 'Price Alerts & Watchlist',
    description: 'Custom targets, monitoring & notification system',
    icon: Bell,
    color: 'rose',
    route: '/price-alerts',
    category: 'strategy',
    bullets: [
      'Set price above/below targets for any ticker symbol',
      'Percentage change alerts with custom thresholds',
      'Personal notes & context on each watchlist entry',
      'Cloud-persisted watchlist synced across all sessions',
      'Email alert notifications when targets are triggered',
    ],
  },
  {
    id: 'risk-dashboard',
    title: 'Risk Dashboard',
    description: 'Portfolio risk scoring, VaR & exposure analysis',
    icon: ShieldAlert,
    color: 'red',
    route: '/risk-dashboard',
    category: 'analysis',
    bullets: [
      'Composite risk score (0–100) with visual gauge indicator',
      'Value at Risk (VaR 95%) and max drawdown estimation',
      'Concentration risk & currency exposure pie charts',
      'Sector & geographic diversification analysis',
      'Automated risk warnings with actionable mitigation advice',
    ],
  },
  {
    id: 'correlation-matrix',
    title: 'Correlation Matrix',
    description: 'Position correlation heatmap & diversification analysis',
    icon: Grid3X3,
    color: 'cyan',
    route: '/correlation-matrix',
    category: 'analysis',
    bullets: [
      'Pearson correlation heatmap across all positions',
      'Configurable lookback period (3M to 2Y)',
      'Top correlated & anti-correlated pair insights',
      'Average correlation score for diversification assessment',
    ],
  },
  {
    id: 'earnings-calendar',
    title: 'Earnings Calendar',
    description: 'Upcoming earnings, estimates & surprise tracking',
    icon: CalendarDays,
    color: 'orange',
    route: '/earnings-calendar',
    category: 'market',
    bullets: [
      'Earnings dates for all portfolio holdings with timing indicators',
      'EPS & revenue estimates vs actuals with surprise % chart',
      'Beat/miss tracking with historical accuracy trends',
      'Pre-market / after-hours timing & market-cap context',
      'FMP & Nasdaq data sources with fallback badge indicator',
    ],
  },
  {
    id: 'macro-indicators',
    title: 'Critical Macro Indicators',
    description: 'Geopolitical, inflation, energy & valuation dashboards',
    icon: Globe,
    color: 'indigo',
    route: '/macro-indicators',
    category: 'market',
    bullets: [
      'Polymarket geopolitical prediction odds (US–Iran, diplomacy)',
      'Truflation real-time CPI index & MacroMicro YoY charts',
      'TradingView charts: TTF Gas, ES Futures, S5INFT, Qatar, US02Y',
      'S&P 500 Forward P/E, Shiller CAPE & BofA cash allocation',
      'External service login hub (Polymarket, Truflation, TradingView)',
    ],
  },
  {
    id: 'capitol-trades',
    title: 'Capitol Trades',
    description: 'Congressional stock trading disclosures (STOCK Act)',
    icon: Landmark,
    color: 'amber',
    route: '/capitol-trades',
    category: 'market',
    bullets: [
      'Real-time congressional trades from capitoltrades.com',
      'Buy/sell type, issuer, ticker, size & price details',
      'Party, chamber & politician breakdown with color coding',
      'Direct links to individual trade detail pages',
    ],
  },
  {
    id: 'stock-price-gathering',
    title: 'Stock Price Gathering Dashboard',
    description: 'Yahoo Finance live quotes from your Excel watchlist',
    icon: FileBarChart,
    color: 'sky',
    route: '/stock-price-gathering',
    category: 'analysis',
    bullets: [
      'Upload FileList (Symbol col A, ISIN col B)',
      'Auto-refresh every 10 min — price, currency, 52W H/L',
      'ISIN-aware exchange resolution — no API key, no hallucinations',
      'Export to EST workbook → realtimedata tab cols G/H/I',
    ],
  },
  {
    id: 'weak-sentiments',
    title: 'Weak Sentiments',
    description: 'Multi-LLM weak signal detection with verification guardrails',
    icon: Eye,
    color: 'violet',
    route: '/weak-sentiments',
    category: 'market',
    bullets: [
      '6 AI models scanning for early-stage sentiment shifts',
      'Triple-loop verification: no hallucinations, no guessing',
      'Progressive results with data quality scoring per model',
      'Save & reload research sessions with timestamps',
    ],
  },
  {
    id: 'wiseboard',
    title: 'WiseBoard — AI Boardroom',
    description: 'Consulting-grade boardroom: from board question to Go / Conditional-Go / Pilot / No-Go decision',
    icon: Briefcase,
    color: 'fuchsia',
    route: '/wiseboard',
    category: 'strategy',
    bullets: [
      { bold: 'Governance & Framing', text: '(P0–P1B): pre-flight audit, option typology, investment thesis, lateral challenge & falsifiers' },
      { bold: 'Evidence Spine', text: '(P2 Pass 1 & 2): sourced facts with confidence, freshness & single-source flags — no hallucinated numbers' },
      { bold: '9 Specialist lanes', text: '(P3–P11): macro, scenarios, Porter, commercial, ecosystem, operations, finance, risk & culture — with fact-sufficiency scoring' },
      { bold: 'Synthesis & Decision', text: '(P20–P21): auto-aggregated board pack, MECE recommendation with stance + 3 supporting points' },
      { bold: 'Sales Activation', text: '(P22): target accounts & go-to-market plan, gated by P21 stance' },
      { bold: 'Outcomes:', text: 'defensible board decision, full audit trail, compliance traffic lights, assumptions log & per-case PDF export' },
    ],
  },
];
